import type { EndpointOverlay } from './overlay';

// Japanese endpoint pages.

// Parameter descriptions shared across many endpoints. Endpoint-specific `params`
// entries override these.
const commonParams: Record<string, string> = {
  limit: '1 ページあたりの件数。',
  sort: '並べ替えに使うフィールド。',
  order: '並び順：asc（昇順）または desc（降順）。',
  adjusted: '権利落ち調整を反映するかどうか。既定値は true。',
  ts_code: 'TS 証券コード。データセットによってはカンマ区切りで複数指定できます。',
  trade_date: '取引日（YYYYMMDD 形式）。',
  start_date: '開始日（当日を含む、YYYYMMDD 形式）。',
  end_date: '終了日（当日を含む、YYYYMMDD 形式）。',
  fields: 'カンマ区切りの出力フィールド。省略時は既定のフィールドを返します。',
};

const aggregateParams: Record<string, string> = {
  stocksTicker: '大文字・小文字を区別するティッカーシンボル。',
  optionsTicker: '標準形式のオプション契約ティッカー。',
  multiplier: '時間軸に掛ける倍率。',
  timespan: '集計単位：minute、hour、day、week、month、quarter、year。',
  from: '期間の開始（YYYY-MM-DD または Unix ミリ秒タイムスタンプ）。',
  to: '期間の終了（YYYY-MM-DD または Unix ミリ秒タイムスタンプ）。',
  adjusted: '株式分割調整を反映するかどうか。既定値は true。',
  sort: 'タイムスタンプ順の並び：asc または desc。',
  limit: '取得する基礎集計の上限。既定 5,000、最大 50,000。',
};

const tickParams: Record<string, string> = {
  order: '並べ替えフィールドの順序：asc または desc。',
  limit: '1 ページあたりの件数。既定 1,000、最大 50,000。',
  sort: '並べ替えに使うフィールド。既定は timestamp。',
};

export const endpointsJa: Record<string, EndpointOverlay> = {
  'us-stocks-custom-bars': {
    title: 'カスタムバー（OHLC）',
    summary: '任意の期間のヒストリカル OHLC と出来高。',
    description:
      '米国株について、プレマーケット・通常取引・アフターマーケットの各セッションを含む、調整可能な集計バーを取得します。ある期間にデータがない場合は、その期間に条件を満たす約定がなかったことを意味します。',
    useCases: ['チャート描画', 'テクニカル分析', 'バックテスト', '市場調査'],
    params: aggregateParams,
  },
  'us-options-custom-bars': {
    title: 'カスタムバー（OHLC）',
    summary: '個別オプション契約のヒストリカル集計バー。',
    description:
      '指定したオプション契約について、任意の期間の OHLC、出来高、約定件数、VWAP を取得します。標準形式の O: 契約ティッカーを使用してください。',
    useCases: ['オプションのチャート描画', 'ボラティリティ調査', 'バックテスト', '契約分析'],
    params: aggregateParams,
  },
  'us-options-trades': {
    title: '約定',
    summary: 'オプション契約のティックレベルの約定履歴。',
    description:
      '指定したオプション契約について、任意の時間範囲のティックレベル約定データを取得します。各レコードには価格、数量、取引所、約定条件、正確なタイムスタンプが含まれ、日中分析、市場マイクロストラクチャ研究、バックテスト、監査可能な市場活動の履歴に利用できます。',
    useCases: ['日中分析', 'アルゴリズム取引', '市場マイクロストラクチャ研究', 'データ整合性とコンプライアンス'],
    notes: [
      '設定された Options Advanced のアップストリームからリアルタイムで配信されます。',
      '全履歴は 2014 年 6 月 2 日まで遡ります。',
      'タイムスタンプはナノ秒精度の Unix タイムスタンプです。',
    ],
    params: {
      ...tickParams,
      optionsTicker: 'O: プレフィックス付きの標準オプション契約ティッカー。',
      timestamp:
        '約定タイムスタンプで絞り込みます。YYYY-MM-DD 形式の日付またはナノ秒タイムスタンプを指定でき、.gt、.gte、.lt、.lte 修飾子に対応します。',
    },
  },
  'us-options-quotes': {
    title: '気配',
    summary: 'オプション契約のヒストリカルな売り気配・買い気配。',
    description:
      '指定したオプション契約について、任意の時間範囲のヒストリカル気配を取得します。各レコードには売り気配・買い気配の価格と数量、取引所 ID、正確なタイムスタンプが含まれ、その時点のオプション市場の状況を表します。',
    useCases: ['ヒストリカル気配の分析', '市場の関心度の評価', 'アルゴリズムのバックテスト', '戦略の改善'],
    notes: [
      '設定された Options Advanced のアップストリームからリアルタイムで配信されます。',
      '全履歴は 2022 年 3 月 7 日まで遡ります。',
      '気配数量は単元（通常 100 株）で表されます。',
    ],
    params: {
      ...tickParams,
      optionsTicker: 'O: プレフィックス付きの標準オプション契約ティッカー。',
      timestamp:
        '気配タイムスタンプで絞り込みます。YYYY-MM-DD 形式の日付またはナノ秒タイムスタンプを指定でき、.gt、.gte、.lt、.lte 修飾子に対応します。',
    },
  },
  'us-trades': {
    title: '約定',
    summary: '米国株のティックレベルの約定履歴。',
    description:
      '指定した米国株について、任意の時間範囲の完全なティックレベル約定データを取得します。各レコードには価格、数量、取引所、約定条件、ナノ秒精度のタイムスタンプが含まれます。この粒度のデータは集計バーの構築や市場マイクロストラクチャ分析の基礎となり、OHLC に算入されるすべての適格約定を網羅します。',
    useCases: ['日中分析', 'アルゴリズム取引', '市場マイクロストラクチャ研究', 'データ整合性とコンプライアンス'],
    notes: [
      '最上位プランでは 2003 年 9 月 10 日まで遡って参照できます。',
      'タイムスタンプはナノ秒精度の Unix タイムスタンプです。',
    ],
    params: {
      ...tickParams,
      stockTicker: '大文字・小文字を区別する株式ティッカー。',
      timestamp:
        '約定タイムスタンプで絞り込みます。YYYY-MM-DD 形式の日付またはナノ秒タイムスタンプを指定でき、.gt、.gte、.lt、.lte 修飾子に対応します。',
    },
  },
  'us-sma': {
    title: '単純移動平均（SMA）',
    summary: '指定したウィンドウの単純移動平均。',
    description:
      '指定したティッカーについて、任意の時間範囲の単純移動平均（SMA）を取得します。SMA は一定期間の価格を平均して変動をならし、基調となるトレンドやシグナルを浮かび上がらせます。',
    useCases: [
      'トレンド分析',
      '売買シグナル（SMA クロス）',
      'サポート・レジスタンスの判定',
      'エントリーとエグジットのタイミング',
    ],
    notes: ['日次集計にウィンドウ 10 を組み合わせると 10 日移動平均になります。'],
    params: {
      stockTicker: '大文字・小文字を区別する株式ティッカー。',
      timestamp:
        'タイムスタンプで絞り込みます。YYYY-MM-DD 形式の日付またはミリ秒タイムスタンプを指定でき、.gt、.gte、.lt、.lte 修飾子に対応します。',
      timespan: '集計単位：minute、hour、day、week、month、quarter、year。',
      adjusted: '使用する集計に株式分割調整を反映するかどうか。既定値は true。',
      window: 'SMA を計算するウィンドウ幅。既定は 50。',
      series_type: '各集計で使用する価格：open、high、low、close。既定は close。',
      expand_underlying: '計算に使用した集計データをレスポンスに含めるかどうか。',
      order: 'タイムスタンプ順の並び：asc または desc。既定は desc。',
      limit: '1 ページあたりの件数。既定 10、最大 5,000。',
    },
  },
  'us-news': {
    title: 'ニュース',
    summary: 'センチメント分析付きの最新ニュース。',
    description:
      '指定したティッカーに関連する最新のニュース記事を、要約、配信元情報、センチメント分析とあわせて取得します。金融ニュースを集約し、関連ティッカーを抽出し、センチメントを付与して原典へのリンクを提供するため、市場心理や企業動向の把握に役立ちます。',
    useCases: ['市場センチメント分析', '投資リサーチ', '自動モニタリング', 'ポートフォリオ戦略の最適化'],
    params: {
      ticker: '大文字・小文字を区別する株式ティッカー。絞り込み修飾子に対応します。',
      published_utc:
        '指定した日時に公開された、またはそれ以前・以降の結果を返します。.gt、.gte、.lt、.lte 修飾子に対応します。',
      order: '並べ替えフィールドの順序：asc または desc。',
      limit: '1 ページあたりの件数。既定 10、最大 1,000。',
      sort: '並べ替えに使うフィールド。既定は published_utc。',
    },
  },
  'us-options-contracts': {
    title: '全契約',
    summary: '有効および満期を迎えたオプション契約のインデックス。',
    description:
      '有効な契約と満期済みの契約を網羅したインデックスを取得します。全体を横断して照会することも、特定の原資産銘柄に限定することもできます。各レコードには契約タイプ（コール／プット）、権利行使方式、満期日、権利行使価格が含まれ、市場での提供状況の分析やオプション戦略の研究に利用できます。',
    useCases: ['提供状況の分析', '戦略開発', 'リサーチとモデリング', '契約の探索'],
    notes: [
      '非推奨となった ticker パラメータは使用できません。個別契約の照会にはオプション契約エンドポイントをご利用ください。',
    ],
    params: {
      underlying_ticker: '特定の原資産銘柄に関連する契約を照会します。絞り込み修飾子に対応します。',
      contract_type: '契約タイプで絞り込みます：call、put、other。',
      expiration_date: '満期日（YYYY-MM-DD）で絞り込みます。絞り込み修飾子に対応します。',
      as_of: '指定日（YYYY-MM-DD）時点の契約の状態。既定は当日。',
      strike_price: '権利行使価格で絞り込みます。絞り込み修飾子に対応します。',
      expired: '満期済みの契約を照会します。既定は false。',
      order: '並べ替えフィールドの順序：asc または desc。',
      limit: '1 ページあたりの件数。既定 10、最大 1,000。',
      sort: '並べ替えに使うフィールド。既定は ticker。',
    },
  },
  'us-balance-sheets': {
    title: '貸借対照表',
    summary: '四半期・年次の財政状態のスナップショット。',
    description:
      '上場企業が提出した資産、負債、資本を標準化した形で取得します。各レコードは会計期間時点のスナップショットであり、期間累計のフローではありません。',
    useCases: ['ファンダメンタル分析', 'バリュエーション', '負債分析', '個別株リサーチ'],
    params: {
      cik: 'SEC の Central Index Key（CIK）。',
      tickers: 'ティッカー配列にこのティッカーを含むレコードで絞り込みます。',
      period_end: '期末日（YYYY-MM-DD）。.gt、.gte、.lt、.lte を付けられます。',
      filing_date: '当該期間を含む直近の SEC 提出日。比較修飾子に対応します。',
      fiscal_year: '会計年度。.gt、.gte、.lt、.lte を付けられます。',
      fiscal_quarter: '会計四半期（1〜4）。',
      timeframe: '報告周期：quarterly または annual。',
      limit: '1 ページあたりの件数。既定 100、最大 50,000。',
      sort: 'カンマ区切りのフィールドに .asc または .desc を付けて指定します。',
    },
  },
  'us-financial-ratios': {
    title: '財務レシオ',
    summary: 'バリュエーション、収益性、流動性、レバレッジの各指標。',
    description:
      '直近の取引日を基準に、過去 12 か月（TTM）の財務データと日次の市場価格から算出した企業のレシオを取得します。',
    useCases: ['企業バリュエーション', '同業他社比較', '財務健全性', '銘柄スクリーニング'],
    notes: ['数値型のクエリフィールドは .gt、.gte、.lt、.lte などの比較修飾子に対応します。'],
    params: {
      ticker: '株式ティッカー。絞り込み修飾子に対応します。',
      cik: 'SEC の Central Index Key（CIK）。絞り込み修飾子に対応します。',
      price: '算出に用いた株価。',
      average_volume: '直近 30 取引日の平均出来高。',
      market_cap: '時価総額。',
      earnings_per_share: '普通株主に帰属する加重平均 1 株当たり利益。',
      price_to_earnings: '株価を正の 1 株当たり利益で割った値。',
      price_to_book: '株価を 1 株当たり純資産で割った値。',
      price_to_sales: '株価を 1 株当たり売上高で割った値。',
      price_to_cash_flow: '株価を正の 1 株当たり営業キャッシュフローで割った値。',
      price_to_free_cash_flow: '株価を正の 1 株当たりフリーキャッシュフローで割った値。',
      dividend_yield: '1 株当たり年間配当を株価で割った値。',
      return_on_assets: '純利益を総資産で割った値。',
      return_on_equity: '純利益を自己資本で割った値。',
      debt_to_equity: '短期・長期の負債を自己資本で割った値。',
      current: '流動資産を流動負債で割った値。',
      quick: '流動資産から棚卸資産を除いて流動負債で割った値。',
      cash: '現金及び現金同等物を流動負債で割った値。',
      ev_to_sales: '企業価値を売上高で割った値。',
      ev_to_ebitda: '企業価値を EBITDA で割った値。',
      enterprise_value: '時価総額に負債を加え、現金及び現金同等物を差し引いた値。',
      free_cash_flow: '営業キャッシュフローから設備投資を差し引いた値。',
      limit: '1 ページあたりの件数。既定 100、最大 50,000。',
      sort: 'カンマ区切りのフィールドに .asc または .desc を付けて指定します。既定は ticker.asc。',
    },
  },
  'cn-daily': {
    title: '日次株価',
    summary: '未調整の A 株日次 OHLC と売買状況。',
    description:
      '取引終了後に公開される A 株の日次データです。売買停止銘柄には行がありません。価格は未調整のため、必要に応じて調整係数のデータセットと組み合わせてください。',
    useCases: ['日次スクリーニング', 'ヒストリカル分析', 'マーケットブレッド', 'バックテスト'],
    notes: [
      '全市場を取得する場合は、銘柄コードではなく取引日ごとに繰り返し取得してください。',
      '日付は YYYYMMDD 形式です。',
    ],
    params: commonParams,
  },
  'cn-cyq-perf': {
    title: 'コスト分布と勝率',
    summary: '日次の保有コスト分布と勝率。',
    description:
      '日次の保有コストの分位数、過去レンジ、加重平均コスト、含み益となっている保有比率を取得します。データは 2018 年以降です。',
    useCases: ['コスト分析', '需給シグナル', 'サポート・レジスタンス', '日次スクリーニング'],
    notes: ['1 回の呼び出しで最大 6,000 行です。ページングするか、照会する日付範囲を狭めてください。'],
    params: { ...commonParams, ts_code: 'TS 証券コード（必須）。' },
  },
  'cn-technical-factors': {
    title: 'テクニカルファクター',
    summary: '日次の調整済み価格とクオンツ向けテクニカル指標。',
    description:
      'A 株向けに生成された全履歴の日次価格ファクターとテクニカル指標を取得します。指標は前方調整済み価格をもとに算出されます。',
    useCases: ['ファクター研究', 'シグナル生成', 'トレンド分析', 'クオンツによる銘柄選定'],
    notes: [
      '1 回の呼び出しで最大 10,000 行です。ページングするか、照会範囲を狭めてください。',
      '前方調整済みの履歴は直近取引日を基準とした日次スナップショットであり、遡って更新されることはありません。',
      '動的な pro_bar の調整はリクエストの end_date を基準とするため、結果が異なる場合があります。',
      'テクニカル指標は前方調整済み価格をもとに算出されます。',
    ],
    params: commonParams,
  },
  'cn-balance-sheet': {
    title: '貸借対照表',
    summary: '個別の A 株上場企業が提出した貸借対照表。',
    description:
      '上場企業の貸借対照表科目の履歴を取得します。一般事業会社、銀行、保険会社、証券会社それぞれの報告様式に対応しています。',
    useCases: ['ファンダメンタル分析', '支払能力の調査', '四半期スクリーニング', '企業バリュエーション'],
    notes: [
      '1 リクエストにつき 1 社のみです。全市場の四半期データを取得する場合は、日付を主軸とした上位データセットをご利用ください。',
    ],
    params: {
      ts_code: 'TS 証券コード。このデータセットは 1 リクエストにつき 1 社のみです。',
      ann_date: '公告日（YYYYMMDD 形式）。',
      start_date: '公告日の範囲の開始。',
      end_date: '公告日の範囲の終了。',
      period: '報告期末。例：20251231 は年次報告書。',
      report_type: '報告書の種類。',
      comp_type: '企業区分：1 一般事業、2 銀行、3 保険、4 証券。',
      fields: 'カンマ区切りの出力フィールド。',
    },
  },
  'cn-disclosure-date': {
    title: '開示スケジュール',
    summary: '定期報告書の予定・実際の開示日。',
    description:
      '定期報告書の開示スケジュールを取得します。予定開示日、直近の公告日、各報告期の実際の開示日が含まれ、決算発表の予測やイベントカレンダーの作成に適しています。',
    useCases: ['決算カレンダー', 'イベントドリブン戦略', 'カバレッジの追跡', '決算シーズンの計画'],
    notes: ['1 回の呼び出しで最大 6,000 行です。', '日付は YYYYMMDD 形式です。'],
    params: {
      ts_code: 'TS 証券コード。',
      end_date:
        '報告期末（YYYYMMDD 形式。例：20181231 は 2018 年度の年次報告、20180630 は中間報告）。',
      pre_date: '予定開示日（YYYYMMDD 形式）。',
      ann_date: '直近の開示公告日（YYYYMMDD 形式）。',
      actual_date: '実際の開示日（YYYYMMDD 形式）。',
    },
  },
  'indices-custom-bars': {
    title: 'カスタムバー（OHLC）',
    summary: '任意の期間で集計した指数のヒストリカル値。',
    description:
      '指数について、任意の期間と時間軸（米国東部時間）で集計した始値・高値・安値・終値を取得します。株式やオプションと異なり、指数の集計は個々の約定ではなく公表された指数値から作られるため、新しい指数値がない期間にはバーが生成されません。',
    useCases: ['ベンチマーク比較', '市場トレンド分析', 'データ可視化', 'リサーチとモデリング'],
    params: {
      indicesTicker: '標準形式の I: プレフィックス付き指数ティッカー。',
      multiplier: '時間軸に掛ける倍率。',
      timespan: '集計単位：minute、hour、day、week、month、quarter、year。',
      from: '期間の開始（YYYY-MM-DD または Unix ミリ秒タイムスタンプ）。',
      to: '期間の終了（YYYY-MM-DD または Unix ミリ秒タイムスタンプ）。',
      sort: 'タイムスタンプ順の並び：asc または desc。',
      limit: '取得する基礎集計の上限。既定 5,000、最大 50,000。',
    },
    notes: [
      '指数ティッカーは標準形式の I: プレフィックスを使い、リクエストは自動的に指数のエンタイトルメントへ振り分けられます。',
      '指数バーは約定から作られないため、出来高・VWAP・約定件数は含まれません。',
    ],
  },
  'indices-previous-day-bar': {
    title: '前営業日バー',
    summary: '指数の前営業日の OHLC。',
    description:
      '指数の前営業日の始値・高値・安値・終値を取得します。日付範囲を自分で計算しなくても、直前セッションの基準値をそのまま得られます。',
    useCases: ['基準値の比較', '日次レポート', 'テクニカル分析', '市場調査'],
    params: {
      indicesTicker: '標準形式の I: プレフィックス付き指数ティッカー。',
    },
    notes: ['「前営業日」は直近の完了したセッションを指し、ザラ場中に変化することはありません。'],
  },
  'indices-daily-summary': {
    title: '日次サマリー',
    summary: '指定日における指数の始値と終値。',
    description:
      '指定日の指数の始値と終値に加え、その日の高値・安値、およびプレマーケットとアフターマーケットで算出された値を取得します。時間外に値を公表する算出方法を持つ指数のみ、該当フィールドが返されます。',
    useCases: ['日次パフォーマンス分析', 'ヒストリカルデータの収集', '時間外の把握', 'ポートフォリオ管理'],
    params: {
      indicesTicker: '標準形式の I: プレフィックス付き指数ティッカー。',
      date: 'セッション日（YYYY-MM-DD）。',
    },
    notes: ['指数の通常取引時間は米国東部時間の月曜〜金曜 9:30〜16:00 です。'],
  },
  'indices-snapshot': {
    title: '指数スナップショット',
    summary: '複数指数の現在値とセッションの動きを一度に取得。',
    description:
      '複数の指数のスナップショットを取得します。現在値、当日の始値・高値・安値、前日終値、前日比の変化額と変化率が含まれます。1 回のリクエストでウォッチリスト全体を賄えるため、指数ごとにポーリングせずに最新状態を保ちたいダッシュボードに適しています。',
    useCases: ['市況の把握', '市場センチメントの追跡', 'ダッシュボード表示', 'ポートフォリオの文脈把握'],
    params: {
      'ticker.any_of': 'カンマ区切りの指数ティッカーのリスト。',
      ticker: '単一の指数ティッカー。.gt、.gte、.lt、.lte による範囲指定に対応します。',
      order: '並び順：asc または desc。',
      limit: '1 ページあたりの件数。既定 10、最大 250。',
      sort: '並べ替えに使うフィールド。既定は ticker。',
    },
    notes: ['ticker.any_of を使うと 1 回のリクエストでウォッチリスト全体を取得できます。'],
  },
  'indices-tickers': {
    title: '指数ティッカー一覧',
    summary: '検索可能な指数シンボルのディレクトリ。',
    description:
      'Eroya が対応する指数シンボルを、指数名・配信元・稼働状況とあわせて取得します。共通のティッカーディレクトリを market=indices で絞り込むと指数のみが返り、S&P、Nasdaq、ダウ・ジョーンズ、FTSE Russell、MSCI、モーニングスター、Cboe Global Indices、ソシエテ・ジェネラルなど 10,000 を超える指数をカバーします。',
    useCases: ['指数の探索', 'シンボルの検証', 'ウォッチリスト作成', 'データ連携'],
    params: {
      market: '指数を一覧するには indices を指定します。',
      ticker: '特定の指数シンボルを指定します。.gt、.gte、.lt、.lte に対応します。',
      search: '指数名に対する大文字小文字を区別しない部分一致検索。',
      active: '現在も配信されているかどうか。既定値は true。',
      order: '並び順：asc または desc。',
      limit: '1 ページあたりの件数。既定 100、最大 1,000。',
      sort: '並べ替えに使うフィールド。既定は ticker。',
    },
    notes: ['リクエストを指数のエンタイトルメントへ振り分けるのは market=indices です。指定しない場合は株式のティッカーが返ります。'],
  },
  'us-income-statements': {
    title: '損益計算書',
    summary: '四半期・年次の収益、費用、利益のフロー。',
    description:
      '上場企業が提出した損益計算書を標準化した形で取得します。貸借対照表と異なり、これらは会計期間にわたるフローであるため、同じ年度の四半期レコードと年次レコードは異なる期間を表し、1 本の時系列に混在させることはできません。',
    useCases: ['ファンダメンタル分析', '業績トレンド調査', '利益率分析', 'スクリーニングとモデリング'],
    params: {
      cik: 'SEC の Central Index Key（CIK）。',
      tickers: 'ティッカー配列にこのティッカーを含むレコードで絞り込みます。',
      period_end: '期末日（YYYY-MM-DD）。.gt、.gte、.lt、.lte を付けられます。',
      filing_date: '当該期間を含む直近の SEC 提出日。比較修飾子に対応します。',
      fiscal_year: '会計年度。.gt、.gte、.lt、.lte を付けられます。',
      fiscal_quarter: '会計四半期（1〜4）。',
      timeframe: '報告周期：quarterly または annual。',
      limit: '1 ページあたりの件数。既定 100、最大 50,000。',
      sort: 'カンマ区切りのフィールドに .asc または .desc を付けて指定します。',
    },
    notes: ['四半期と年次は対象期間が異なります。時系列を作る前に timeframe で絞り込んでください。'],
  },
  'us-cash-flow-statements': {
    title: 'キャッシュフロー計算書',
    summary: '期間ごとの営業・投資・財務キャッシュフロー。',
    description:
      '上場企業が提出したキャッシュフロー計算書を標準化した形で取得します。キャッシュフローは会計方針の影響を最も受けにくい計算書であり、報告利益の質を検証する手段として広く使われます。',
    useCases: ['利益の質の検証', 'フリーキャッシュフローのモデリング', '資本配分のリサーチ', 'バリュエーション'],
    params: {
      cik: 'SEC の Central Index Key（CIK）。',
      tickers: 'ティッカー配列にこのティッカーを含むレコードで絞り込みます。',
      period_end: '期末日（YYYY-MM-DD）。.gt、.gte、.lt、.lte を付けられます。',
      filing_date: '当該期間を含む直近の SEC 提出日。比較修飾子に対応します。',
      fiscal_year: '会計年度。.gt、.gte、.lt、.lte を付けられます。',
      fiscal_quarter: '会計四半期（1〜4）。',
      timeframe: '報告周期：quarterly または annual。',
      limit: '1 ページあたりの件数。既定 100、最大 50,000。',
      sort: 'カンマ区切りのフィールドに .asc または .desc を付けて指定します。',
    },
  },
  'us-short-interest': {
    title: '空売り残高',
    summary: '月 2 回報告される空売り残高と買い戻し日数。',
    description:
      '銘柄ごとの空売り残高を決済日単位で取得します。買い戻し日数（days to cover）の算出に使われる平均出来高も含まれます。取引所は月に 2 回この残高を集計するため、この系列は日次の指標ではなく決済日時点のスナップショットです。',
    useCases: ['ポジションの偏りとスクイーズ分析', 'センチメント調査', 'リスクスクリーニング', 'イベントスタディ'],
    params: {
      ticker: 'ティッカーシンボル。フィルタ修飾子に対応します。',
      settlement_date: '決済日（YYYY-MM-DD）。.gt、.gte、.lt、.lte を付けられます。',
      days_to_cover: '買い戻し日数で絞り込みます。比較修飾子に対応します。',
      limit: '1 ページあたりの件数。既定 100、最大 50,000。',
      sort: 'カンマ区切りのフィールドに .asc または .desc を付けて指定します。既定は ticker.asc。',
    },
    notes: ['残高は月 2 回報告され、決済日から数営業日遅れて公表されます。'],
  },
  'us-short-volume': {
    title: '空売り出来高',
    summary: '日次の空売り出来高と総出来高に占める割合。',
    description:
      '銘柄ごとの日次の空売り出来高を、比較対象となる総出来高とあわせて取得します。日次の空売り比率をそのまま計算できます。これは売買動向の指標であり、月 2 回報告される決済日ベースの空売り残高とは別物です。',
    useCases: ['日次センチメントの追跡', 'フロー分析', 'シグナル構築', '流動性調査'],
    params: {
      ticker: 'ティッカーシンボル。フィルタ修飾子に対応します。',
      date: '取引日（YYYY-MM-DD）。.gt、.gte、.lt、.lte を付けられます。',
      short_volume_ratio: '空売り比率で絞り込みます。比較修飾子に対応します。',
      limit: '1 ページあたりの件数。既定 100、最大 50,000。',
      sort: 'カンマ区切りのフィールドに .asc または .desc を付けて指定します。既定は ticker.asc。',
    },
    notes: ['空売り比率にはザラ場のマーケットメイクも含まれるため、それ自体が弱気シグナルというわけではありません。'],
  },
  'flatfiles-datasets': {
    title: 'フラットファイルのデータセット一覧',
    summary: 'すべてのフラットファイルデータセットとご利用プランの権限。',
    description:
      'フラットファイルのカタログを返します。他のフラットファイルエンドポイントで使えるデータセット ID、アーカイブのパス、データの粒度、利用に必要な最低プラン、現在のキーで利用できるかどうかが含まれます。ダウンロード自体に従量制限はありません — プランはどのデータセットを利用できるかだけを決め、バイト単位の課金は行いません。',
    useCases: ['データセットの探索', '権限の確認', 'パイプライン設定', 'プランの比較'],
    notes: [
      '日次・分次データセットは Pro プラン以上、ティックの約定と気配は Premium プラン以上でご利用いただけます。',
      'dataset パラメータには、データセット ID とアーカイブの完全なパスのどちらでも指定できます。',
    ],
  },
  'flatfiles-list': {
    title: 'フラットファイルの一覧',
    summary: '指定期間に公開されたファイルとそのサイズ。',
    description:
      '指定した期間にデータセットで公開されたフラットファイルを、各オブジェクトの実バイト数とあわせて一覧します。実際に取引があった立会日だけが含まれるため、ダウンロード前に対象日を洗い出し、転送量をあらかじめ見積もる確実な方法になります。',
    useCases: ['ヒストリカルデータの補完計画', '転送量の見積もり', '増分同期', '欠損の検出'],
    params: {
      dataset: 'データセット ID またはアーカイブのパス。',
      start: '期間の開始日（当日を含む、YYYY-MM-DD 形式）。',
      end: '期間の終了日（当日を含む、YYYY-MM-DD 形式）。省略時は start と同じ日になります。',
    },
    notes: [
      '1 回の照会で指定できる期間は 366 日までです。',
    ],
  },
  'flatfiles-url': {
    title: '署名付きダウンロード URL の取得',
    summary: '1 立会日分のファイルへの短時間有効な直リンク。',
    description:
      '1 立会日分のファイルに対して署名付きの HTTPS URL を返します。標準的な S3 署名付き URL なので、あらゆる HTTP クライアント、S3 クライアント、データフレームライブラリからそのまま読み取ることができ、Range リクエストにも対応します。Eroya はリンクを発行するだけでファイル本体を中継しないため、転送速度はお客様の回線に依存します。',
    useCases: [
      '直接ダウンロード',
      'Polars や DuckDB での部分読み取り',
      'ヒストリカルデータの並列補完',
      'データウェアハウスへの取り込み',
    ],
    params: {
      dataset: 'データセット ID またはアーカイブのパス。',
      date: '立会日（YYYY-MM-DD 形式）。',
      expiresIn: 'リンクの有効秒数。60〜3600、既定は 900。',
    },
    notes: [
      '各リンクは有効期間中、1 つのファイルのみを許可します。必要になった時点で再取得し、長期保存はお控えください。',
      '各立会日のファイルは翌営業日の米国東部時間 11:00 頃に公開されます。それより早く要求すると 404 が返ります。',
    ],
  },
  'flatfiles-download': {
    title: 'フラットファイルのダウンロード',
    summary: '署名付きファイルへ直接リダイレクトします。',
    description:
      '署名付き URL のエンドポイントと同じ処理ですが、JSON を返す代わりに HTTP 302 で署名付き URL へ直接リダイレクトします。リダイレクトに追従するクライアントであれば、Eroya の URL をそのままファイルとして扱えるため、署名処理を自前で実装する必要はありません。',
    useCases: ['curl と wget', 'シェルのパイプライン', 'pandas での直接読み込み', 'Airflow や cron のスケジュール実行'],
    params: {
      dataset: 'データセット ID またはアーカイブのパス。',
      date: '立会日（YYYY-MM-DD 形式）。',
      expiresIn: 'リンクの有効秒数。60〜3600、既定は 900。',
    },
    notes: [
      'curl では -L を、requests では allow_redirects を有効にして、クライアントがリダイレクトに追従するようにしてください。',
      'リダイレクト先は署名済みの単一オブジェクトです。保存せずにその場で追従してください。',
    ],
  },
};
